import { TableCellComponentProps } from '@/shared/types/table';
import { cn } from '@/shared/utils/cn';
import { Avatar, AvatarSize } from '@/modules/Avatar';
import { isValidUrl } from '@/modules/Avatar/utils';
import {
  getTableCellClasses,
  getTableCellProps,
  handleCellClick,
  isFlexColumn,
} from '../../utils/cellStyles';

const AVATAR_SIZE: AvatarSize = 'sm';

const isImageSource = (value?: string) => {
  if (!value) return false;
  if (value.startsWith('data:') || value.startsWith('blob:')) {
    return true;
  }

  return isValidUrl(value);
};

const getLetterFromValue = (value: unknown): string => {
  if (typeof value === 'string' && value.trim().length > 0) {
    return value.trim();
  }

  return '?';
};

export const AvatarsCell = ({
  value,
  column,
  state = 'default',
  className,
  onCellFocus,
  stickyLeft = 0,
  isLastLocked = false,
  isRowEditing,
  hasColumnMaxWidthAuto = false,
}: TableCellComponentProps) => {
  const baseClasses = getTableCellClasses(
    state,
    className,
    column.locked,
    isLastLocked,
    undefined,
    isRowEditing,
    false,
    column.width !== undefined || isFlexColumn(column, hasColumnMaxWidthAuto)
  );
  const cellProps = getTableCellProps(column, stickyLeft, undefined, false, hasColumnMaxWidthAuto);

  const avatars = Array.isArray(value) ? value : [value];
  const maxVisible = 5;
  const remaining = Math.max(0, avatars.length - maxVisible);

  const hasFixedWidth = column.width !== undefined;
  return (
    <td className={baseClasses} {...cellProps} onClick={(e) => handleCellClick(e, onCellFocus)}>
      <div
        className={cn(
          'ui-flex ui-items-center ui--space-x-4 ui-pr-4',
          hasFixedWidth && 'ui-min-w-0 ui-overflow-hidden'
        )}
      >
        {avatars.slice(0, maxVisible).map((avatar: unknown, index: number) => {
          const avatarObj =
            typeof avatar === 'object' && avatar !== null
              ? (avatar as Record<string, unknown>)
              : null;
          const source = avatarObj
            ? (avatarObj.src as string | undefined)
            : (avatar as string | undefined);
          const name = avatarObj
            ? (avatarObj.name as string | undefined)
            : (avatar as string | undefined);

          if (typeof source === 'string' && isImageSource(source)) {
            return (
              <Avatar
                key={index}
                type="people"
                src={source}
                size={AVATAR_SIZE}
                className="ui-flex-shrink-0 ui-border ui-border-default"
              />
            );
          }

          const letter = getLetterFromValue(name).slice(0, 3);

          return (
            <Avatar
              key={index}
              type="letter"
              text={letter}
              size={AVATAR_SIZE}
              className="ui-flex-shrink-0 ui-border ui-border-default"
            />
          );
        })}
        {remaining > 0 && (
          <div className="ui-flex ui-h-8 ui-min-h-[32px] ui-w-8 ui-min-w-[32px] ui-flex-shrink-0 ui-items-center ui-justify-center ui-rounded-control-pill ui-border ui-border-default ui-bg-fill-brand ui-text-sm ui-font-normal ui-leading-[20.5px] ui-tracking-[0.5px] ui-text-on-color">
            +{remaining}
          </div>
        )}
      </div>
    </td>
  );
};
